import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { CalendarDays } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { EngineSettingsCard } from '~/components/routes/forecast-engine-settings/engine-settings-card'
import { ENGINE_SETTINGS_READ } from '~/components/routes/forecast-engine-settings/use-engine-settings'
import { QueryState, QueryStateEmpty } from '~/components/new/app-ui/query-state'
import { Bone } from '~/components/ui/skeleton'
import { cn } from '~/lib/utils'
import { api } from '~/lib/tuyau'
import { usePermissionGuard, withRoutePermissions, type PermissionRequirement } from '~/utils/rbac'
import { ReglagesModal } from '../previsions-pilotage/-reglages/reglages-modal'
import { PeriodsTable, PeriodsTableSkeleton } from './-periods-table'
import { periodDot, shortLabel, type SchoolHolidayPeriod } from './-shared'
import { YearTimeline, YearTimelineSkeleton } from './-year-timeline'

const SCHOOL_HOLIDAYS_READ: PermissionRequirement = ['read', 'previsions.calendar.school-holidays']

/** Légende du chargement : la largeur du libellé de chaque période, de la Toussaint à l'été. */
const LEGEND_SKELETON_WIDTHS = ['9ch', '4ch', '5ch', '9ch', '3ch']

const LegendChip = ({ dotClassName, children }: { dotClassName: string; children: ReactNode }) => (
  <span className="flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs text-muted-foreground">
    <span className={cn('size-2 rounded-full', dotClassName)} />
    {children}
  </span>
)

const Stat = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="rounded-lg border bg-card px-4 py-3">
    <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-1 text-lg font-semibold tabular-nums">{value}</p>
  </div>
)

/** Chiffres de l'en-tête de page ; `null` pendant le chargement (os à la place des valeurs). */
const Stats = ({
  values,
}: {
  values: { periods: number; schoolYears: number; daysPerZone: number } | null
}) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
    <Stat label="Périodes" value={values?.periods ?? <Bone width="2ch" />} />
    <Stat label="Années scolaires" value={values?.schoolYears ?? <Bone width="1ch" />} />
    <Stat label="Zones" value="A · B · C" />
    <Stat label="Jours / zone / an" value={values?.daysPerZone ?? <Bone width="2ch" />} />
  </div>
)

const HolidaysContent = ({ periods }: { periods: SchoolHolidayPeriod[] }) => {
  const schoolYears = [...new Set(periods.map((period) => period.schoolYear))].filter(
    (schoolYear): schoolYear is string => schoolYear !== null,
  )
  return (
    <>
      <Stats
        values={{
          periods: periods.length,
          schoolYears: schoolYears.length,
          daysPerZone: Math.round(
            periods.reduce((total, period) => total + period.days, 0) /
              Math.max(1, schoolYears.length * 3),
          ),
        }}
      />

      {schoolYears.map((schoolYear) => (
        <YearTimeline
          key={schoolYear}
          schoolYear={schoolYear}
          periods={periods.filter((period) => period.schoolYear === schoolYear)}
        />
      ))}

      <PeriodsTable periods={periods} />
    </>
  )
}

/** Chargement : chiffres, deux années (l'en cours et la suivante) et le détail, sans données. */
const HolidaysSkeleton = () => (
  <>
    <Stats values={null} />
    <YearTimelineSkeleton />
    <YearTimelineSkeleton />
    <PeriodsTableSkeleton />
  </>
)

/** Calendrier des vacances scolaires : en-tête, chiffres, frise par année et détail. */
const SchoolHolidaysSection = () => {
  const query = useQuery(api.calendar.schoolHolidays.queryOptions({}))

  const periods: SchoolHolidayPeriod[] = query.data?.periods ?? []
  const labels = [...new Set(periods.map((period) => period.label))]
  const fetchedAt = periods.find((period) => period.fetchedAt !== null)?.fetchedAt ?? null

  return (
    <>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <CalendarDays className="size-5" />
          </span>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Vacances scolaires</h1>
            <p className="text-sm text-muted-foreground">
              Zones A, B et C — source data.education.gouv.fr
              {fetchedAt !== null &&
                `, récupérées le ${new Date(fetchedAt).toLocaleDateString('fr-FR')}`}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {query.isPending
            ? LEGEND_SKELETON_WIDTHS.map((width, index) => (
                <LegendChip key={index} dotClassName="bg-muted">
                  <Bone width={width} />
                </LegendChip>
              ))
            : labels.map((label) => (
                <LegendChip key={label} dotClassName={periodDot(label)}>
                  {shortLabel(label)}
                </LegendChip>
              ))}
        </div>
      </header>

      <QueryState
        query={query}
        skeleton={<HolidaysSkeleton />}
        errorFallback="Impossible de charger les périodes."
        empty={{
          when: (data) => data.periods.length === 0,
          content: (
            <QueryStateEmpty>
              Aucune période en base. Lance{' '}
              <code className="rounded bg-muted px-1.5 py-0.5">
                node ace calendar:sync-school-holidays
              </code>
              .
            </QueryStateEmpty>
          ),
        }}
      >
        {(data) => <HolidaysContent periods={data.periods} />}
      </QueryState>
    </>
  )
}

/**
 * Onglet « Paramètres » de la rubrique Prévision : la carte « Réglages du moteur » en tête, puis le
 * calendrier des vacances scolaires. Chaque partie suit son propre droit ; l'onglet s'ouvre avec
 * l'un ou l'autre. La carte ouvre l'éditeur unique des réglages : la modale Réglages du Pilotage,
 * sur sa partie moteur.
 */
const VacancesScolairesPage = () => {
  const canReadEngine = usePermissionGuard(ENGINE_SETTINGS_READ).authorized
  const canReadHolidays = usePermissionGuard(SCHOOL_HOLIDAYS_READ).authorized
  const [reglages, setReglages] = useState(false)
  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-6">
      {canReadEngine && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <EngineSettingsCard onOpen={() => setReglages(true)} />
          <ReglagesModal open={reglages} onClose={() => setReglages(false)} initialView="moteur" />
        </div>
      )}
      {canReadHolidays && <SchoolHolidaysSection />}
    </div>
  )
}

/**
 * Calendrier des vacances scolaires — ressource propre depuis l'audit du 19/09 (BLOQ-08) : l'écran
 * n'avait aucune garde et aucun point d'entrée. Liaison `none` : le calendrier est national.
 */
export const Route = createFileRoute('/_authenticated/vacances-scolaires/')({
  staticData: {
    permission: { any: [SCHOOL_HOLIDAYS_READ, ENGINE_SETTINGS_READ] },
  },
  component: VacancesScolairesPage,
})
withRoutePermissions(Route)
